import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useShippingLabels } from '../hooks/useShippingLabels';
import { useSenderAddress } from '../hooks/useSenderAddress';
import { useWarehouseAddress } from '../hooks/use-warehouse-address';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import { classifyOrderIssue } from '../utils/classifyOrderIssue';
import type { OrderIssueSource } from '../types/orderIssues';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { LabelReprintModal } from '../components/labels/LabelReprintModal';
import { getSupportedCountriesWithFallback, POSTAL_CODE_FORMATS, getPostalCodeMismatchWarning, type RecipientAddress, type LabelStatus, type ShopifyLabelSyncResult, type ReturnLabelSender } from '../types/shipping';
import { getCountryRequirements, validatePostalCodeForCountry, normalizePostalCodeForCountry } from '../utils/countryRequirements';
import { Tag, Package, Printer, Download, ExternalLink, Search, Filter, ChevronLeft, ChevronRight, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, Circle as XCircle, Clock, Copy, Settings, RefreshCw, Flag, Sparkles, RotateCcw, Warehouse } from 'lucide-react';
import { format } from 'date-fns';
import { useAiAddress, type AiNormalizeResult } from '../hooks/useAiAddress';

type ShopifyOrderLookupResult = {
  shopifyOrderId: number;
  orderName: string;
  recipientName: string;
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
  addressExtra?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone?: string;
  region?: string;
  formattedAddressLines?: string[];
  warnings?: string[];
  reference: string;
  financialStatus?: string;
  fulfillmentStatus?: string;
};

export const ShippingLabels: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const adminId = getAdminId(user);
  const {
    labels,
    loading,
    creating,
    error,
    total,
    page,
    pageSize,
    filters,
    setPage,
    applyFilters,
    createLabel,
    cancelLabel,
    updatePrintCount,
    refreshLabels,
  } = useShippingLabels();
  const { senderAddress, isConfigured, loading: loadingSender } = useSenderAddress();
  const { warehouseAddress, isConfigured: warehouseConfigured } = useWarehouseAddress();
  const { processing: aiProcessing, lastResult: aiResult, aiError, normalizeAddress, clearResult: clearAiResult } = useAiAddress();
  const [aiProcessed, setAiProcessed] = useState(false);

  const [recipient, setRecipient] = useState<RecipientAddress>({
    name: '',
    street: '',
    houseNumber: '',
    houseNumberExt: '',
    postalCode: '',
    city: '',
    countryCode: 'NL',
    phone: '',
    region: '',
  });
  const [reference, setReference] = useState('');
  const [shopifyOrderRef, setShopifyOrderRef] = useState('');
  const [shopifySearching, setShopifySearching] = useState(false);
  const [shopifySearchError, setShopifySearchError] = useState<string | null>(null);
  const [shopifySearchResults, setShopifySearchResults] = useState<ShopifyOrderLookupResult[]>([]);
  const [selectedShopifyOrder, setSelectedShopifyOrder] = useState<ShopifyOrderLookupResult | null>(null);
  const [createdLabel, setCreatedLabel] = useState<{
    barcode: string;
    trackingCode: string;
    trackingUrl: string;
    pdfBase64: string;
    shopifySync?: ShopifyLabelSyncResult;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [reprintModalOpen, setReprintModalOpen] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [reportingIssue, setReportingIssue] = useState(false);
  const [reportIssueFeedback, setReportIssueFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [postalCodeError, setPostalCodeError] = useState<string | null>(null);
  const [countryMismatchWarning, setCountryMismatchWarning] = useState<{
    suggestedCountry: { code: string; name: string };
    message: string;
  } | null>(null);

  // Return mode: the warehouse becomes the recipient and the sender is either
  // ours (blind) or the customer's, looked up from the order number.
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnSenderMode, setReturnSenderMode] = useState<'own' | 'customer'>('own');
  const [customerSender, setCustomerSender] = useState<ReturnLabelSender>({
    name: '',
    street: '',
    houseNumber: '',
    houseNumberExt: '',
    postalCode: '',
    city: '',
    countryCode: 'NL',
    phone: '',
  });
  const [senderPostalCodeError, setSenderPostalCodeError] = useState<string | null>(null);
  const [detectedClientName, setDetectedClientName] = useState<string | null>(null);

  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      setShopifyOrderRef(refParam);
    }
  }, [searchParams]);

  const countryReq = getCountryRequirements(recipient.countryCode);
  const currentPostalFormat = POSTAL_CODE_FORMATS[recipient.countryCode];
  const postalCodeRequired = countryReq.postalRequired;
  const houseNumberRequired = countryReq.houseNumberRequired;
  const regionRequired = countryReq.regionRequired;

  const selectedShopifyReq = getCountryRequirements(selectedShopifyOrder?.countryCode || recipient.countryCode);
  const selectedShopifyHouseNumberRequired = selectedShopifyReq.houseNumberRequired;
  const selectedShopifyRegionRequired = selectedShopifyReq.regionRequired;

  const handlePostalCodeChange = (value: string) => {
    const upperValue = value.toUpperCase();
    const coerced = normalizePostalCodeForCountry(upperValue, recipient.countryCode);
    setRecipient({ ...recipient, postalCode: coerced });

    if (coerced.length > 0) {
      const validation = validatePostalCodeForCountry(coerced, recipient.countryCode);
      setPostalCodeError(validation.valid ? null : validation.error || null);

      const mismatch = getPostalCodeMismatchWarning(coerced, recipient.countryCode);
      setCountryMismatchWarning(mismatch);
    } else {
      setPostalCodeError(null);
      setCountryMismatchWarning(null);
    }
  };

  const handleCountryChange = (countryCode: string) => {
    setRecipient({ ...recipient, countryCode, postalCode: '', region: '' });
    setPostalCodeError(null);
    setCountryMismatchWarning(null);
  };

  const handleAcceptSuggestedCountry = () => {
    if (countryMismatchWarning) {
      const newCountryCode = countryMismatchWarning.suggestedCountry.code;
      setRecipient({ ...recipient, countryCode: newCountryCode });
      setCountryMismatchWarning(null);
      const validation = validatePostalCodeForCountry(recipient.postalCode, newCountryCode);
      setPostalCodeError(validation.valid ? null : validation.error || null);
    }
  };

  const applyRecipientPrefill = (next: RecipientAddress) => {
    setRecipient(next);
    if (next.postalCode) {
      const validation = validatePostalCodeForCountry(next.postalCode, next.countryCode);
      setPostalCodeError(validation.valid ? null : validation.error || null);
      const mismatch = getPostalCodeMismatchWarning(next.postalCode, next.countryCode);
      setCountryMismatchWarning(mismatch);
    } else {
      setPostalCodeError(null);
      setCountryMismatchWarning(null);
    }
  };

  /**
   * The warehouse is written into `recipient` rather than kept beside it, so the
   * existing postal validation and create payload keep working untouched. The
   * server re-reads `users.warehouse_*` regardless, so this is a preview only.
   */
  const recipientFromWarehouse = (): RecipientAddress => ({
    name: warehouseAddress?.name || warehouseAddress?.contactName || '',
    street: warehouseAddress?.street || '',
    houseNumber: warehouseAddress?.houseNumber || '',
    houseNumberExt: warehouseAddress?.houseNumberExt || '',
    addressExtra: '',
    postalCode: warehouseAddress?.postalCode || '',
    city: warehouseAddress?.city || '',
    countryCode: warehouseAddress?.countryCode || 'NL',
    phone: warehouseAddress?.phone || '',
    region: '',
  });

  const enterReturnMode = () => {
    setIsReturnMode(true);
    setReturnSenderMode('own');
    setDetectedClientName(null);
    setSenderPostalCodeError(null);
    setCreatedLabel(null);
    applyRecipientPrefill(recipientFromWarehouse());
  };

  const exitReturnMode = () => {
    setIsReturnMode(false);
    setReturnSenderMode('own');
    setDetectedClientName(null);
    setSenderPostalCodeError(null);
    setCustomerSender({
      name: '',
      street: '',
      houseNumber: '',
      houseNumberExt: '',
      postalCode: '',
      city: '',
      countryCode: 'NL',
      phone: '',
    });
    applyRecipientPrefill({
      name: '',
      street: '',
      houseNumber: '',
      houseNumberExt: '',
      addressExtra: '',
      postalCode: '',
      city: '',
      countryCode: 'NL',
      phone: '',
      region: '',
    });
  };

  /** Sibling of applyRecipientPrefill, so the outbound path stays byte-identical. */
  const applyCustomerSenderPrefill = (next: ReturnLabelSender) => {
    setCustomerSender(next);
    if (next.postalCode) {
      const validation = validatePostalCodeForCountry(next.postalCode, next.countryCode);
      setSenderPostalCodeError(validation.valid ? null : validation.error || null);
    } else {
      setSenderPostalCodeError(null);
    }
  };

  const handleSenderPostalCodeChange = (value: string) => {
    const coerced = normalizePostalCodeForCountry(value.toUpperCase(), customerSender.countryCode);
    setCustomerSender({ ...customerSender, postalCode: coerced });
    if (!coerced) {
      setSenderPostalCodeError(null);
      return;
    }
    const validation = validatePostalCodeForCountry(coerced, customerSender.countryCode);
    setSenderPostalCodeError(validation.valid ? null : validation.error || null);
  };

  const handleSelectShopifyOrder = async (order: ShopifyOrderLookupResult) => {
    setSelectedShopifyOrder(order);
    setShopifySearchError(null);
    setAiProcessed(false);
    clearAiResult();

    const baseAddress = {
      name: order.recipientName || '',
      street: order.street || '',
      houseNumber: order.houseNumber || '',
      houseNumberExt: order.houseNumberExt || '',
      addressExtra: order.addressExtra || '',
      postalCode: normalizePostalCodeForCountry(order.postalCode || '', order.countryCode),
      city: order.city || '',
      countryCode: order.countryCode || '',
      phone: order.phone || '',
      region: order.region || '',
    };

    // In return mode the looked-up client becomes the sender, not the recipient:
    // the recipient stays on the warehouse.
    if (isReturnMode) {
      applyCustomerSenderPrefill({
        name: baseAddress.name,
        street: baseAddress.street,
        houseNumber: baseAddress.houseNumber,
        houseNumberExt: baseAddress.houseNumberExt,
        postalCode: baseAddress.postalCode,
        city: baseAddress.city,
        countryCode: baseAddress.countryCode || 'NL',
        phone: baseAddress.phone,
      });
      const hasUsableAddress = Boolean(baseAddress.street && baseAddress.city);
      setReturnSenderMode(hasUsableAddress ? 'customer' : 'own');
      setDetectedClientName(baseAddress.name || null);
    } else {
      applyRecipientPrefill(baseAddress);
    }
    setReference(order.reference || order.orderName || '');

    const rawShopify = order.formattedAddressLines
      ? { formatted: order.formattedAddressLines, name: order.recipientName, city: order.city, zip: order.postalCode, provinceCode: order.region, countryCodeV2: order.countryCode, phone: order.phone }
      : undefined;

    const result = await normalizeAddress(baseAddress, rawShopify, {
      shopifyOrderId: order.shopifyOrderId,
      orderNumber: order.reference || order.orderName || undefined,
      source: 'shipping_labels',
    });
    if (result) {
      const n = result.normalized;
      // The AI pass is what splits a Shopify one-line street into street plus
      // house number, so a customer sender needs it just as much as a recipient.
      if (isReturnMode) {
        const senderCountry = n.countryCode || baseAddress.countryCode || 'NL';
        applyCustomerSenderPrefill({
          name: n.name || baseAddress.name,
          street: n.street || baseAddress.street,
          houseNumber: n.houseNumber ?? baseAddress.houseNumber,
          houseNumberExt: n.houseNumberExt ?? baseAddress.houseNumberExt,
          postalCode: normalizePostalCodeForCountry(n.postalCode || baseAddress.postalCode, senderCountry),
          city: n.city || baseAddress.city,
          countryCode: senderCountry,
          phone: n.phone || baseAddress.phone,
        });
        setDetectedClientName(n.name || baseAddress.name || null);
        if (n.street && n.city) setReturnSenderMode('customer');
      } else {
        applyRecipientPrefill({
          name: n.name || baseAddress.name,
          street: n.street || baseAddress.street,
          houseNumber: n.houseNumber ?? baseAddress.houseNumber,
          houseNumberExt: n.houseNumberExt ?? baseAddress.houseNumberExt,
          addressExtra: n.addressExtra ?? baseAddress.addressExtra,
          postalCode: normalizePostalCodeForCountry(n.postalCode || baseAddress.postalCode, n.countryCode || baseAddress.countryCode),
          city: n.city || baseAddress.city,
          countryCode: n.countryCode || baseAddress.countryCode,
          phone: n.phone || baseAddress.phone,
          region: n.region || baseAddress.region,
        });
      }
      setAiProcessed(true);

      const actionableIssues = result.diagnostics?.issues?.filter(i => i.severity === 'error' || i.severity === 'warning') || [];
      const orderRef = order.reference || order.orderName || '';
      // The address was only used as a return sender, so flagging it would
      // pollute the issue queue with problems that do not affect delivery.
      if (actionableIssues.length > 0 && adminId && orderRef && !isReturnMode) {
        const issueDesc = 'AI detected: ' + actionableIssues.map(i => `${i.field}: ${i.message}`).join('; ');
        const hasError = actionableIssues.some(i => i.severity === 'error');
        supabase.rpc('upsert_order_issue', {
          p_admin_id: adminId,
          p_order_reference: orderRef,
          p_shopify_order_id: order.shopifyOrderId ?? null,
          p_client_name: order.recipientName || null,
          p_recipient_address: {
            name: n.name ?? baseAddress.name,
            street: n.street ?? baseAddress.street,
            houseNumber: n.houseNumber ?? baseAddress.houseNumber,
            postalCode: n.postalCode ?? baseAddress.postalCode,
            city: n.city ?? baseAddress.city,
            countryCode: n.countryCode ?? baseAddress.countryCode,
            phone: n.phone ?? baseAddress.phone,
            region: n.region ?? baseAddress.region,
          },
          p_issue_type: hasError ? 'address_validation_error' : 'incomplete_address',
          p_issue_description: issueDesc,
          p_error_code: null,
          p_source: 'ai_address_validation' as OrderIssueSource,
        }).then(() => {}, () => {});
      }
    }
  };

  const handleClearShopifyOrder = () => {
    setSelectedShopifyOrder(null);
    setShopifySearchResults([]);
    setShopifySearchError(null);
    setAiProcessed(false);
    clearAiResult();
  };

  const handleSearchShopifyOrder = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setShopifySearchError('Not authenticated');
      return;
    }

    const term = shopifyOrderRef.trim();
    if (!term) {
      setShopifySearchError('Please enter an order reference (e.g. #1001)');
      return;
    }

    setShopifySearching(true);
    setShopifySearchError(null);
    setShopifySearchResults([]);
    setSelectedShopifyOrder(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-search-order-by-number`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderNumber: term }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Search failed');
      }

      const orders: ShopifyOrderLookupResult[] = Array.isArray(result.orders) ? result.orders : [];
      setShopifySearchResults(orders);

      if (orders.length === 0) {
        setShopifySearchError(result?.message || "Reference doesn't exist. Please recheck for correct ref.");
        return;
      }

      if (orders.length === 1) {
        handleSelectShopifyOrder(orders[0]);
      }
    } catch (err) {
      setShopifySearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setShopifySearching(false);
    }
  };

  const handleCreateLabel = async (e: React.FormEvent) => {
    e.preventDefault();

    if (postalCodeRequired && !recipient.postalCode.trim()) {
      setPostalCodeError(`${countryReq.postalLabel} is required`);
      return;
    }

    const normalizedPostal = normalizePostalCodeForCountry(recipient.postalCode.trim(), recipient.countryCode);
    const recipientForCreate = { ...recipient, postalCode: normalizedPostal };

    const postalValidation = validatePostalCodeForCountry(normalizedPostal, recipient.countryCode);
    if (!postalValidation.valid) {
      setPostalCodeError(postalValidation.error || `Invalid ${countryReq.postalLabel}`);
      return;
    }

    if (isReturnMode && returnSenderMode === 'customer') {
      if (!customerSender.name.trim() || !customerSender.street.trim() || !customerSender.city.trim()) {
        setSenderPostalCodeError(null);
        setReportIssueFeedback({ type: 'error', message: 'The customer sender needs at least a name, street and city.' });
        return;
      }
      if (senderPostalCodeError) return;
    }

    setRecipient(recipientForCreate);

    const result = await createLabel({
      recipient: recipientForCreate,
      reference: reference || undefined,
      shopifyOrderId: selectedShopifyOrder?.shopifyOrderId,
      isReturn: isReturnMode,
      sender: isReturnMode && returnSenderMode === 'customer' ? customerSender : undefined,
    });

    const orderRef = reference || selectedShopifyOrder?.orderName || '';

    if (result.success && result.barcode) {
      setCreatedLabel({
        barcode: result.barcode,
        trackingCode: result.trackingCode || result.barcode,
        trackingUrl: result.trackingUrl || '',
        pdfBase64: result.pdfBase64 || '',
        shopifySync: result.shopifySync,
      });

      // Printing a return does not fix the delivery problem that opened the issue.
      if (adminId && orderRef && !isReturnMode) {
        (async () => { try { await supabase.rpc('resolve_order_issue_by_reference', {
          p_admin_id: adminId,
          p_order_reference: orderRef,
        }); } catch {} })();
      }
      // A failed return label is our problem, not a customer delivery issue.
    } else if (adminId && orderRef && result.error && !isReturnMode) {
      (async () => { try { await supabase.rpc('upsert_order_issue', {
        p_admin_id: adminId,
        p_order_reference: orderRef,
        p_shopify_order_id: selectedShopifyOrder?.shopifyOrderId ?? null,
        p_client_name: recipient.name || null,
        p_recipient_address: {
          name: recipient.name,
          street: recipient.street,
          houseNumber: recipient.houseNumber,
          postalCode: recipientForCreate.postalCode,
          city: recipient.city,
          countryCode: recipient.countryCode,
          phone: recipient.phone,
          region: recipient.region,
        },
        p_issue_type: classifyOrderIssue(result.error),
        p_issue_description: result.error,
        p_error_code: null,
        p_source: 'shipping_labels_manual' as OrderIssueSource,
      }); } catch {} })();
    }
  };

  const handleReportIssue = async () => {
    if (!adminId) return;
    const orderRef = reference || selectedShopifyOrder?.orderName || '';
    // In return mode `recipient` holds our own warehouse, so the client is the
    // customer sender instead — recording the warehouse as the client would be wrong.
    const issueParty = isReturnMode
      ? { name: customerSender.name, street: customerSender.street, houseNumber: customerSender.houseNumber, postalCode: customerSender.postalCode, city: customerSender.city, countryCode: customerSender.countryCode, phone: customerSender.phone, region: '' }
      : { name: recipient.name, street: recipient.street, houseNumber: recipient.houseNumber, postalCode: recipient.postalCode, city: recipient.city, countryCode: recipient.countryCode, phone: recipient.phone, region: recipient.region };

    if (!orderRef && !issueParty.name) {
      setReportIssueFeedback({ type: 'error', message: 'Enter an order reference or recipient name first.' });
      return;
    }

    setReportingIssue(true);
    setReportIssueFeedback(null);
    try {
      const { error: rpcError } = await supabase.rpc('upsert_order_issue', {
        p_admin_id: adminId,
        p_order_reference: orderRef || `MANUAL-${Date.now()}`,
        p_shopify_order_id: selectedShopifyOrder?.shopifyOrderId ?? null,
        p_client_name: issueParty.name || selectedShopifyOrder?.recipientName || null,
        p_recipient_address: issueParty,
        p_issue_type: 'other' as const,
        p_issue_description: 'Manually reported by worker from Shipping Labels page',
        p_error_code: null,
        p_source: 'shipping_labels_manual' as OrderIssueSource,
      });
      if (rpcError) throw rpcError;
      setReportIssueFeedback({ type: 'success', message: 'Order flagged for review.' });
      setTimeout(() => setReportIssueFeedback(null), 4000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to report issue';
      setReportIssueFeedback({ type: 'error', message: msg });
    } finally {
      setReportingIssue(false);
    }
  };

  const handlePrint = (pdfBase64: string, labelId?: string) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }

    if (labelId) {
      updatePrintCount(labelId);
    }
  };

  const handleDownload = (pdfBase64: string, barcode: string) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `shipping-label-${barcode}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReprint = (labelId: string) => {
    setSelectedLabelId(labelId);
    setReprintModalOpen(true);
  };

  const handleCopyBarcode = (barcode: string) => {
    navigator.clipboard.writeText(barcode);
  };

  const resetForm = () => {
    setRecipient({
      name: '',
      street: '',
      houseNumber: '',
      houseNumberExt: '',
      addressExtra: '',
      postalCode: '',
      city: '',
      countryCode: 'NL',
      phone: '',
      region: '',
    });
    setReference('');
    setShopifyOrderRef('');
    setShopifySearching(false);
    setShopifySearchError(null);
    setShopifySearchResults([]);
    setSelectedShopifyOrder(null);
    setCreatedLabel(null);
    setPostalCodeError(null);
    setCountryMismatchWarning(null);
    setAiProcessed(false);
    setIsReturnMode(false);
    setReturnSenderMode('own');
    setDetectedClientName(null);
    setSenderPostalCodeError(null);
    setCustomerSender({
      name: '',
      street: '',
      houseNumber: '',
      houseNumberExt: '',
      postalCode: '',
      city: '',
      countryCode: 'NL',
      phone: '',
    });
    clearAiResult();
  };

  const handleSearch = () => {
    applyFilters({ ...filters, search: searchTerm });
  };

  const handleStatusFilter = (status: LabelStatus | 'all') => {
    applyFilters({ ...filters, status });
  };

  const handleDateFilter = (dateRange: 'all' | 'today' | 'week' | 'month' | 'custom') => {
    applyFilters({ ...filters, dateRange });
  };

  const getStatusBadge = (status: LabelStatus) => {
    switch (status) {
      case 'generated':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            <Clock className="h-3 w-3" />
            Generated
          </span>
        );
      case 'printed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
            <CheckCircle className="h-3 w-3" />
            Printed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            <XCircle className="h-3 w-3" />
            Cancelled
          </span>
        );
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loadingSender) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Shipping Labels
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Create and manage PostNL shipping labels for your packages
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <Tag className="h-5 w-5 text-cyan-600" />
              Create New Label
            </h2>

            {!isConfigured && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 dark:text-amber-300">
                      Sender address not configured
                    </h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      You need to configure your sender address before creating labels.
                    </p>
                    <Link
                      to="/settings"
                      className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
                    >
                      <Settings className="h-4 w-4" />
                      Go to Settings
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {createdLabel ? (
              <div className="space-y-6">
                <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-800 dark:text-green-300">
                        Label Created Successfully
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-400">
                        Your shipping label is ready
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Barcode</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                          {createdLabel.barcode}
                        </span>
                        <button
                          onClick={() => handleCopyBarcode(createdLabel.barcode)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          <Copy className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {createdLabel.shopifySync?.attempted && (
                    <div
                      className={`p-4 border rounded-lg mb-6 ${
                        createdLabel.shopifySync.success
                          ? 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800'
                          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {createdLabel.shopifySync.success ? (
                          <CheckCircle className="h-5 w-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p
                            className={`text-sm font-medium ${
                              createdLabel.shopifySync.success
                                ? 'text-cyan-900 dark:text-cyan-200'
                                : 'text-amber-900 dark:text-amber-200'
                            }`}
                          >
                            {createdLabel.shopifySync.success
                              ? (createdLabel.shopifySync.customerNotified
                                  ? 'Tracking sent to Shopify and customer notified'
                                  : 'Tracking sent to Shopify')
                              : 'Shopify sync failed'}
                          </p>
                          {!createdLabel.shopifySync.success && (
                            <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                              {createdLabel.shopifySync.error || 'Please retry or check Shopify connection/permissions.'}
                            </p>
                          )}
                          {createdLabel.shopifySync.success && !createdLabel.shopifySync.customerNotified && (
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              Customer notification was not sent for this update.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => handlePrint(createdLabel.pdfBase64)}
                      variant="primary"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print Label
                    </Button>
                    <Button
                      onClick={() => handleDownload(createdLabel.pdfBase64, createdLabel.barcode)}
                      variant="secondary"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                    {createdLabel.trackingUrl && (
                      <a
                        href={createdLabel.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary inline-flex items-center"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Track Package
                      </a>
                    )}
                  </div>
                </div>

                <Button onClick={resetForm} variant="secondary" className="w-full">
                  Create Another Label
                </Button>
              </div>
            ) : (
              <form onSubmit={handleCreateLabel} className="space-y-6">
                {isReturnMode && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <RotateCcw className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
                          Return label
                        </p>
                        <p className="text-sm text-indigo-700 dark:text-indigo-400 mt-1">
                          The parcel travels back to your warehouse. Enter an order number to detect the
                          client and use their address as sender, or keep your own address for a blind label.
                          This will not fulfill the order or change its issue status.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <h3 className="text-sm font-medium text-cyan-900 dark:text-cyan-200 mb-3 flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Shopify Order
                  </h3>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      label="Order reference"
                      value={shopifyOrderRef}
                      onChange={(e) => setShopifyOrderRef(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchShopifyOrder();
                        }
                      }}
                      placeholder="#1001"
                    />
                    <div className="sm:pt-6">
                      <Button
                        type="button"
                        onClick={handleSearchShopifyOrder}
                        variant="secondary"
                        loading={shopifySearching}
                        disabled={shopifySearching}
                      >
                        Go
                      </Button>
                    </div>
                  </div>

                  {shopifySearchError && (
                    <p className="mt-3 text-sm text-red-700 dark:text-red-300">
                      {shopifySearchError}
                    </p>
                  )}

                  {selectedShopifyOrder && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Selected: {selectedShopifyOrder.orderName}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Recipient: {selectedShopifyOrder.recipientName || '—'}
                          </p>
                          {(selectedShopifyOrder.fulfillmentStatus || selectedShopifyOrder.financialStatus) && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {selectedShopifyOrder.fulfillmentStatus && (() => {
                                const s = selectedShopifyOrder.fulfillmentStatus;
                                const label = s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                                const cls = s === 'FULFILLED'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : s === 'UNFULFILLED'
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                    : 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300';
                                return (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
                                    {s === 'FULFILLED' ? <CheckCircle className="h-3 w-3" /> : s === 'UNFULFILLED' ? <Clock className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                                    {label}
                                  </span>
                                );
                              })()}
                              {selectedShopifyOrder.financialStatus && (() => {
                                const s = selectedShopifyOrder.financialStatus;
                                const label = s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                                const cls = s === 'PAID'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : s === 'REFUNDED'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                    : s === 'PARTIALLY_REFUNDED'
                                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
                                return (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
                                    {s === 'REFUNDED' || s === 'PARTIALLY_REFUNDED' ? <XCircle className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                                    {label}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          {aiProcessing && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 animate-pulse">
                              <Sparkles className="h-3 w-3" />
                              AI processing…
                            </span>
                          )}
                          {aiProcessed && !aiProcessing && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                              <Sparkles className="h-3 w-3" />
                              AI-processed
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleClearShopifyOrder}
                          className="text-sm font-medium text-cyan-700 dark:text-cyan-300 hover:underline"
                        >
                          Clear
                        </button>
                      </div>

                      {aiResult?.diagnostics?.issues && aiResult.diagnostics.issues.length > 0 && (
                        <div className="mt-3 p-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Sparkles className="h-4 w-4 text-violet-700 dark:text-violet-300 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-violet-900 dark:text-violet-200">AI Address Diagnostics:</p>
                              <ul className="mt-1 list-disc list-inside text-xs text-violet-800 dark:text-violet-300 space-y-0.5">
                                {aiResult.diagnostics.issues.map((issue, idx) => (
                                  <li key={`ai-issue-${idx}`} className={issue.severity === 'error' ? 'text-red-700 dark:text-red-300 font-medium' : ''}>
                                    [{issue.severity}] {issue.field}: {issue.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {aiError && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-xs text-red-700 dark:text-red-300">AI error: {aiError}</p>
                        </div>
                      )}

                      {selectedShopifyOrder.formattedAddressLines &&
                        selectedShopifyOrder.formattedAddressLines.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              Shopify address (formatted)
                            </p>
                            <div className="mt-1 space-y-0.5 text-sm text-gray-900 dark:text-gray-100">
                              {selectedShopifyOrder.formattedAddressLines.map((line, idx) => (
                                <div key={`${selectedShopifyOrder.shopifyOrderId}-addr-${idx}`}>{line}</div>
                              ))}
                            </div>
                          </div>
                        )}

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <div>
                          <span className="font-medium">Street:</span> {selectedShopifyOrder.street || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Extra:</span> {selectedShopifyOrder.addressExtra || '—'}
                        </div>
                        <div>
                          <span className="font-medium">House no.:</span>{' '}
                          {selectedShopifyOrder.houseNumber
                            ? `${selectedShopifyOrder.houseNumber}${selectedShopifyOrder.houseNumberExt ? ` ${selectedShopifyOrder.houseNumberExt}` : ''}`
                            : '—'}
                        </div>
                        <div>
                          <span className="font-medium">Postal:</span> {selectedShopifyOrder.postalCode || '—'}
                        </div>
                        <div>
                          <span className="font-medium">City:</span> {selectedShopifyOrder.city || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Region:</span> {selectedShopifyOrder.region || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Country:</span> {selectedShopifyOrder.countryCode || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span> {selectedShopifyOrder.phone || '—'}
                        </div>
                      </div>

                      {selectedShopifyOrder.warnings && selectedShopifyOrder.warnings.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
                                Please review this address before creating the label:
                              </p>
                              <ul className="mt-1 list-disc list-inside text-xs text-amber-800 dark:text-amber-300 space-y-0.5">
                                {selectedShopifyOrder.warnings.map((w) => (
                                  <li key={`${selectedShopifyOrder.shopifyOrderId}-warn-${w}`}>
                                    {w === 'HOUSE_NUMBER_NOT_DETECTED'
                                      ? (selectedShopifyHouseNumberRequired
                                          ? 'House number could not be detected from Shopify. Please enter it manually.'
                                          : 'House number was not detected from Shopify. This can be normal for some countries — please verify the address.')
                                      : w === 'REGION_REQUIRED_MISSING'
                                        ? (selectedShopifyRegionRequired
                                            ? `${selectedShopifyReq.regionLabel} is required for this destination. Please enter it.`
                                            : `${selectedShopifyReq.regionLabel} is missing. Please verify the address.`)
                                      : w === 'PHONE_MISSING'
                                        ? 'Phone number is missing. It is often required/recommended for delivery and customs — please add it if available.'
                                      : w === 'COUNTRY_CODE_MISSING_OR_UNKNOWN'
                                        ? 'Country code is missing/unknown. Please select the correct country.'
                                        : w === 'MISSING_POSTAL_CODE'
                                        ? `${selectedShopifyReq.postalLabel} is missing. Please verify and enter it.`
                                          : w === 'MISSING_CITY'
                                            ? 'City is missing. Please verify and enter it.'
                                            : w === 'MISSING_STREET'
                                              ? 'Street is missing. Please verify and enter it.'
                                              : w === 'MISSING_RECIPIENT_NAME'
                                                ? 'Recipient name is missing. Please verify and enter it.'
                                                : w === 'NON_LATIN_ADDRESS'
                                                  ? 'Address contains non-Latin characters (e.g. Japanese, Chinese, Korean). PostNL requires Latin (romanized) characters — please replace the address fields with the romanized version before creating the label.'
                                                  : w.split('_').join(' ').toLowerCase()}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!selectedShopifyOrder && shopifySearchResults.length > 1 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Multiple orders found. Select one to auto-fill the label form.
                      </p>
                      <div className="space-y-2">
                        {shopifySearchResults.map((o) => (
                          <button
                            key={o.shopifyOrderId}
                            type="button"
                            onClick={() => handleSelectShopifyOrder(o)}
                            className="w-full text-left p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {o.orderName}
                              </span>
                              <span className="text-xs text-cyan-700 dark:text-cyan-300">
                                Select
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                              {o.recipientName || '—'} • {o.city || '—'} • {o.countryCode || '—'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {isReturnMode ? (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                    <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2 flex items-center gap-2">
                      <Warehouse className="h-4 w-4" />
                      Ships back to (your warehouse)
                    </h3>
                    {warehouseConfigured && warehouseAddress ? (
                      <>
                        <p className="text-sm text-indigo-700 dark:text-indigo-400">
                          {warehouseAddress.name && <span className="font-medium">{warehouseAddress.name}<br /></span>}
                          {warehouseAddress.contactName && <>{warehouseAddress.contactName}<br /></>}
                          {warehouseAddress.street} {warehouseAddress.houseNumber}
                          {warehouseAddress.houseNumberExt && ` ${warehouseAddress.houseNumberExt}`}<br />
                          {warehouseAddress.postalCode} {warehouseAddress.city}, {warehouseAddress.countryCode}
                        </p>
                        <p className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">
                          Read-only. Change it in{' '}
                          <Link to="/settings" className="underline font-medium">Settings</Link>.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-indigo-700 dark:text-indigo-400">
                        No warehouse address configured.{' '}
                        <Link to="/settings" className="underline font-medium">Set it in Settings</Link>.
                      </p>
                    )}
                  </div>
                ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Recipient Address
                  </h3>
                  <div className="space-y-4">
                    <Input
                      label="Recipient Name"
                      value={recipient.name}
                      onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />

                    <Input
                      label="Phone (Optional)"
                      value={recipient.phone || ''}
                      onChange={(e) => setRecipient({ ...recipient, phone: e.target.value })}
                      placeholder="+31 6 12345678"
                    />

                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <Input
                          label="Street"
                          value={recipient.street}
                          onChange={(e) => setRecipient({ ...recipient, street: e.target.value })}
                          placeholder="Hoofdstraat"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          label="No."
                          value={recipient.houseNumber}
                          onChange={(e) => setRecipient({ ...recipient, houseNumber: e.target.value })}
                          placeholder="123"
                          error={
                            houseNumberRequired && selectedShopifyOrder?.warnings?.includes('HOUSE_NUMBER_NOT_DETECTED') && !recipient.houseNumber
                              ? 'House number not detected from Shopify. Please fill it.'
                              : undefined
                          }
                          required={houseNumberRequired}
                        />
                        <Input
                          label="Ext."
                          value={recipient.houseNumberExt}
                          onChange={(e) => setRecipient({ ...recipient, houseNumberExt: e.target.value })}
                          placeholder="A"
                        />
                      </div>
                    </div>

                    <Input
                      label="Building / Apartment (Optional)"
                      value={recipient.addressExtra || ''}
                      onChange={(e) => setRecipient({ ...recipient, addressExtra: e.target.value })}
                      placeholder="e.g. bloco 19 ap 101"
                    />

                    {regionRequired && (
                      <Input
                        label={countryReq.regionLabel}
                        value={recipient.region || ''}
                        onChange={(e) => setRecipient({ ...recipient, region: e.target.value })}
                        placeholder={
                          recipient.countryCode === 'US'
                            ? 'CA'
                            : recipient.countryCode === 'CA'
                              ? 'ON'
                              : recipient.countryCode === 'AE'
                                ? 'Dubai'
                                : ''
                        }
                        required
                      />
                    )}

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {countryReq.postalLabel}
                        </label>
                        <input
                          type="text"
                          value={recipient.postalCode}
                          onChange={(e) => handlePostalCodeChange(e.target.value)}
                          placeholder={currentPostalFormat?.placeholder || '12345'}
                          required={postalCodeRequired}
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                            postalCodeError
                              ? 'border-red-500 dark:border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {currentPostalFormat && !postalCodeError && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Format: {currentPostalFormat.format}
                          </p>
                        )}
                        {postalCodeError && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {postalCodeError}
                          </p>
                        )}
                      </div>
                      <Input
                        label="City"
                        value={recipient.city}
                        onChange={(e) => setRecipient({ ...recipient, city: e.target.value })}
                        placeholder="Amsterdam"
                        required
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Country
                        </label>
                        <select
                          value={recipient.countryCode}
                          onChange={(e) => handleCountryChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select country</option>
                          {getSupportedCountriesWithFallback(recipient.countryCode || undefined).map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {countryMismatchWarning && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-amber-800 dark:text-amber-300">
                              {countryMismatchWarning.message}
                            </p>
                            <button
                              type="button"
                              onClick={handleAcceptSuggestedCountry}
                              className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline"
                            >
                              Switch to {countryMismatchWarning.suggestedCountry.name}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                <Input
                  label="Reference (Optional)"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Order #12345"
                />

                {isReturnMode ? (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Sender (who posts the parcel)
                      </h3>
                      {detectedClientName && (
                        <p className="mt-1 text-xs text-green-700 dark:text-green-400">
                          Client detected: {detectedClientName}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setReturnSenderMode('own')}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          returnSenderMode === 'own'
                            ? 'bg-cyan-600 border-cyan-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Our address (blind)
                      </button>
                      <button
                        type="button"
                        onClick={() => setReturnSenderMode('customer')}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          returnSenderMode === 'customer'
                            ? 'bg-cyan-600 border-cyan-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Customer address
                      </button>
                    </div>

                    {returnSenderMode === 'own' ? (
                      isConfigured && senderAddress ? (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {senderAddress.contactName && `${senderAddress.contactName}, `}
                          {senderAddress.street} {senderAddress.houseNumber}
                          {senderAddress.houseNumberExt && ` ${senderAddress.houseNumberExt}`}, {senderAddress.postalCode} {senderAddress.city}
                        </p>
                      ) : (
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          No sender address configured.{' '}
                          <Link to="/settings" className="underline font-medium">Set it in Settings</Link>{' '}
                          or switch to the customer address.
                        </p>
                      )
                    ) : (
                      <div className="space-y-4">
                        <Input
                          label="Sender Name"
                          value={customerSender.name}
                          onChange={(e) => setCustomerSender({ ...customerSender, name: e.target.value })}
                          placeholder="John Doe"
                        />

                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2">
                            <Input
                              label="Street"
                              value={customerSender.street}
                              onChange={(e) => setCustomerSender({ ...customerSender, street: e.target.value })}
                              placeholder="Hoofdstraat"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              label="No."
                              value={customerSender.houseNumber}
                              onChange={(e) => setCustomerSender({ ...customerSender, houseNumber: e.target.value })}
                              placeholder="123"
                            />
                            <Input
                              label="Ext."
                              value={customerSender.houseNumberExt || ''}
                              onChange={(e) => setCustomerSender({ ...customerSender, houseNumberExt: e.target.value })}
                              placeholder="A"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Postal Code
                            </label>
                            <input
                              type="text"
                              value={customerSender.postalCode}
                              onChange={(e) => handleSenderPostalCodeChange(e.target.value)}
                              placeholder={POSTAL_CODE_FORMATS[customerSender.countryCode]?.placeholder || '12345'}
                              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                                senderPostalCodeError
                                  ? 'border-red-500 dark:border-red-500'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                            {senderPostalCodeError && (
                              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                {senderPostalCodeError}
                              </p>
                            )}
                          </div>
                          <Input
                            label="City"
                            value={customerSender.city}
                            onChange={(e) => setCustomerSender({ ...customerSender, city: e.target.value })}
                            placeholder="Amsterdam"
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Country
                            </label>
                            <select
                              value={customerSender.countryCode}
                              onChange={(e) => setCustomerSender({ ...customerSender, countryCode: e.target.value, postalCode: '' })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            >
                              {getSupportedCountriesWithFallback(customerSender.countryCode || undefined).map((country) => (
                                <option key={country.code} value={country.code}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : isConfigured && senderAddress ? (
                  <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                    <h4 className="text-sm font-medium text-cyan-800 dark:text-cyan-300 mb-2">
                      Sender Address
                    </h4>
                    <p className="text-sm text-cyan-700 dark:text-cyan-400">
                      {senderAddress.contactName && `${senderAddress.contactName}, `}
                      {senderAddress.street} {senderAddress.houseNumber}
                      {senderAddress.houseNumberExt && ` ${senderAddress.houseNumberExt}`}, {senderAddress.postalCode} {senderAddress.city}
                    </p>
                  </div>
                ) : null}

                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                )}

                {reportIssueFeedback && (
                  <div className={`p-3 rounded-lg text-sm ${
                    reportIssueFeedback.type === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                  }`}>
                    {reportIssueFeedback.message}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={creating}
                    disabled={
                      creating ||
                      !!postalCodeError ||
                      (isReturnMode
                        // A customer-sender return never prints our own address,
                        // so shipping_* does not need to be configured for it.
                        ? !warehouseConfigured || (returnSenderMode === 'own' && !isConfigured) || !!senderPostalCodeError
                        : !isConfigured)
                    }
                    className="flex-1 min-w-[10rem]"
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    {isReturnMode ? 'Create Return Label' : 'Create Label'}
                  </Button>

                  {isReturnMode ? (
                    <Button type="button" variant="secondary" onClick={exitReturnMode}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Cancel Return
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={enterReturnMode}
                      disabled={!warehouseConfigured}
                      title={warehouseConfigured ? undefined : 'Set the Warehouse / Returns Address in Settings first'}
                      className="border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Return Label
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="secondary"
                    loading={reportingIssue}
                    disabled={reportingIssue || (!reference && !selectedShopifyOrder && !recipient.name)}
                    onClick={handleReportIssue}
                    className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  >
                    <Flag className="h-4 w-4 mr-2" />
                    Report Issue
                  </Button>
                </div>

                {!isReturnMode && !warehouseConfigured && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Return labels need a warehouse address.{' '}
                    <Link to="/settings" className="underline font-medium">Add it in Settings</Link>.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Clock className="h-5 w-5 text-cyan-600" />
                Label History
              </h2>
              <button
                onClick={() => refreshLabels()}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <RefreshCw className={`h-5 w-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by name, reference, or barcode..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
                <Button onClick={handleSearch} variant="secondary">
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                </div>
                {(['all', 'generated', 'printed', 'cancelled'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(status)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filters.status === status
                        ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Date:</span>
                {(['all', 'today', 'week', 'month'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => handleDateFilter(range)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filters.dateRange === range
                        ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {range === 'all' ? 'All' : range === 'today' ? 'Today' : range === 'week' ? 'This Week' : 'This Month'}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : labels.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No labels found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Create your first shipping label to get started
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Created By
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Date
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Recipient
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Barcode
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Status
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {labels.map((label) => (
                        <tr
                          key={label.id}
                          className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <td className="py-3 px-4">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {label.creator?.full_name || label.creator?.email || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {format(new Date(label.created_at), 'MMM d, yyyy')}
                            </span>
                            <br />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(label.created_at), 'HH:mm')}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {label.recipient_name}
                              </span>
                              {label.label_kind === 'return' && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                                  Return
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {label.recipient_city}, {label.recipient_country_code}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                {label.barcode.slice(0, 12)}...
                              </span>
                              <button
                                onClick={() => handleCopyBarcode(label.barcode)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                title="Copy barcode"
                              >
                                <Copy className="h-3 w-3 text-gray-400" />
                              </button>
                            </div>
                            {label.reference && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Ref: {label.reference}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {getStatusBadge(label.status)}
                            {label.print_count > 0 && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Printed {label.print_count}x
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              {label.status !== 'cancelled' && (
                                <>
                                  <button
                                    onClick={() => handleReprint(label.id)}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Reprint"
                                  >
                                    <Printer className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                  </button>
                                  <a
                                    href={`https://postnl.nl/tracktrace/?B=${label.tracking_code}&P=${label.recipient_postal_code}&D=${label.recipient_country_code}&T=C`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Track"
                                  >
                                    <ExternalLink className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                  </a>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} labels
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {reprintModalOpen && selectedLabelId && (
        <LabelReprintModal
          labelId={selectedLabelId}
          onClose={() => {
            setReprintModalOpen(false);
            setSelectedLabelId(null);
          }}
          onPrinted={() => {
            refreshLabels();
          }}
        />
      )}
    </div>
  );
};
